import { from, fromEvent, Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { finalize, map, mergeMap, reduce } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class FileSelectorService {
    // arrayBuffer' | 'binaryString' | 'dataURL' | 'text' | 'none';
    /**
     * readAs can be 'arrayBuffer', 'binaryString', 'none'(no content available), 'text' (default)
     * accepted files as '.pdf, .doc, .xlsx'
     */
    public getFiles(config: {
        acceptedTypes?: string;
        multiple?: boolean;
        readAs?: 'text' | 'binaryString' | 'dataURL';
    }): Promise<Array<{ file: File; content: string }>>;
    public getFiles(config: {
        acceptedTypes?: string;
        multiple?: boolean;
        readAs?: 'arrayBuffer';
    }): Promise<Array<{ file: File; content: ArrayBuffer }>>;
    public getFiles(config: {
        acceptedTypes?: string;
        multiple?: boolean;
        readAs?: 'none';
    }): Promise<Array<{ file: File; content: null }>>;
    public getFiles(
        config: FileSelectorConfig
    ): Promise<Array<{ file: File; content: any }>> {
        const { acceptedTypes = '', multiple = false, readAs = 'text' } = config;

        // <input type="file" (change)="fileChange($event)" placeholder="Upload file" accept=".pdf,.doc,.docx">
        const input = document.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        input.multiple = multiple;
        input.accept = acceptedTypes;

        const def: {
            promise: Promise<Array<{ file: File; content }>>;
            resolve: (r) => any;
            reject: (e) => any;
        } = {} as any;
        def.promise = new Promise((r, e) => ((def.resolve = r), (def.reject = e)));

        input.onchange = ($event) => {
            const fileList: FileList = ($event as any).target.files;

            def.resolve(
                from(fileList)
                    .pipe(
                        mergeMap((f) => this.readFile(f, readAs)),
                        reduce((a, d: { file: File; content }) => a.concat([d]), []),
                        finalize(() => document.body.removeChild(input))
                    )
                    .toPromise()
            );
        };

        document.body.appendChild(input);
        setTimeout(() => input.click());

        return def.promise;
    }

    public readFile(file: File, readAs: readAsType) {
        return new Observable<{ file: File; content }>((s) => {
            if (readAs === 'none') {
                s.next({ file, content: null });
                s.complete();
                return;
            }

            const reader = new FileReader();
            fromEvent(reader, 'load')
                .pipe(map((e: any) => ({ file, content: e.target.result })))
                .subscribe((r) => {
                    s.next(r);
                    s.complete();
                });
            switch (readAs) {
                case 'arrayBuffer':
                    reader.readAsArrayBuffer(file);
                    break;
                case 'binaryString':
                    reader.readAsBinaryString(file);
                    break;
                case 'dataURL':
                    reader.readAsDataURL(file);
                    break;
                default:
                    reader.readAsText(file);
                    break;
            }
        });
    }
}

export interface FileSelectorConfig {
    /**
     * for example '.pdf, .doc, .xlsx'
     */
    acceptedTypes?: string;
    multiple?: boolean;
    readAs?: readAsType;
}

type readAsType = 'arrayBuffer' | 'binaryString' | 'dataURL' | 'text' | 'none';
